/**
 * Streamdown custom renderer for code blocks that should land in Monaco.
 *
 * Renders apex/java fences with Monaco editor (matching the rest of the app)
 * + workspace action buttons (Add to Workspace, Compare, Show/Hide, Copy).
 *
 * Wired in `chat/DevAgentChatMessageContent.tsx` via:
 *   plugins={{ ..., renderers: [buildApexRenderer({ isStreaming, showCopyButton })] }}
 *
 * Note: Monaco's AMD loader hijacks `window.define` once any Monaco editor
 * mounts. This breaks any *runtime* `await import(...)` calls elsewhere in
 * the app — see FeedChatAgent.tsx and useDownloadAction.ts which were
 * updated to use STATIC imports for that exact reason. If you add new
 * download/export code paths, keep their imports static.
 */
import { useState, useCallback, type MutableRefObject } from "react";
import { useTheme } from "next-themes";
import Editor from "@monaco-editor/react";
import {
  CodeBlockCopyButton,
  CodeBlockExpandToggle,
  CodeBlockCollapsedPreview,
  StreamingIndicator,
  WorkspaceAndCompareButtons,
} from "./chatMessageCodeBlocks/CodeBlockParts";
import { extractApexName } from "./chatCodeDetection";
import { LANG_TO_METADATA_TYPE } from "../devAgentService";
import {
  CHAT_APEX_THEME,
  MONACO_LANG_ALIAS,
  MONACO_LINE_HEIGHT,
  MONACO_MAX_HEIGHT,
  MONACO_OPTIONS,
  MONACO_PADDING,
  beforeChatMonacoMount,
  parseFenceMeta,
  type ApexRendererCtx,
  type CustomRendererProps,
} from "./apexRendererMeta";
import { useApexCodeActions } from "./useApexCodeActions";

function makeApexRenderer(ctxRef: MutableRefObject<ApexRendererCtx>) {
  return function ApexRenderer({ code, language, meta }: CustomRendererProps) {
    const ctx = ctxRef.current;
    const { resolvedTheme } = useTheme();
    const isDarkMode = resolvedTheme === "dark";
    const [copied, setCopied] = useState(false);
    // Default to COLLAPSED ("Show Code") so the chat bubble doesn't blow up
    // when the LLM emits a 200-line apex class. Users click "Show Code" to
    // expand into Monaco. They can collapse again with the same button.
    const [isExpanded, setIsExpanded] = useState(false);

    const codeString = code.replace(/\n$/, "");
    const fenceMeta = parseFenceMeta(meta);
    const codeName = extractApexName(codeString) || fenceMeta.artifactName;
    const lineCount = codeString.split("\n").length;
    const metadataType =
      fenceMeta.metadataType || LANG_TO_METADATA_TYPE[language] || "ApexClass";
    const fileId = fenceMeta.metadataId || fenceMeta.artifactId || codeName || "artifact";
    const compareName = codeName || fenceMeta.artifactName;

    const monacoLanguage = MONACO_LANG_ALIAS[language] || language;
    const {
      handleAddToWorkspace,
      handleCompare,
      showSecondaryAction,
      secondaryActionKind,
      secondaryActionLabel,
      secondaryActionTitle,
    } = useApexCodeActions({
      codeString,
      compareName,
      fenceMeta,
      fileId,
      metadataType,
    });

    const editorHeight = Math.min(
      lineCount * MONACO_LINE_HEIGHT + MONACO_PADDING,
      MONACO_MAX_HEIGHT,
    );

    const handleCopy = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(codeString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.debug("[ApexRenderer] copy failed", err);
      }
    }, [codeString]);

    return (
      <div className="my-4 group relative">
        <div
          className={`relative rounded-lg border border-border/50 overflow-hidden ${
            isDarkMode ? "bg-[#1E1E1E]" : "bg-[#fafafa]"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-1 px-3 py-2 border-b border-border/50 bg-muted/30">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {language}
              </span>
              {compareName && (
                <span className="text-xs font-medium text-foreground/70 truncate max-w-[120px]">
                  {compareName}
                </span>
              )}
              <span className="text-xs text-muted-foreground/80 whitespace-nowrap">
                {lineCount} lines
              </span>
              <StreamingIndicator active={ctx.isStreaming} />
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <CodeBlockCopyButton
                show={ctx.showCopyButton}
                isCopied={copied}
                onCopy={handleCopy}
              />
              <WorkspaceAndCompareButtons
                variant="tagged"
                codeName={compareName}
                showCompare={showSecondaryAction}
                compareKind={secondaryActionKind}
                compareLabel={secondaryActionLabel}
                compareTitle={secondaryActionTitle}
                onAddToWorkspace={handleAddToWorkspace}
                onCompare={handleCompare}
              />
              <CodeBlockExpandToggle
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded((v) => !v)}
              />
            </div>
          </div>

          {!isExpanded && (
            <CodeBlockCollapsedPreview
              codeString={codeString}
              lineCount={lineCount}
              onToggleExpand={() => setIsExpanded(true)}
              detailHint={compareName || undefined}
              showMoreHint={lineCount > 3}
            />
          )}

          {isExpanded && (
            <Editor
              height={`${editorHeight}px`}
              language={monacoLanguage}
              value={codeString}
              theme={isDarkMode ? CHAT_APEX_THEME.dark : CHAT_APEX_THEME.light}
              beforeMount={beforeChatMonacoMount}
              options={MONACO_OPTIONS}
            />
          )}
        </div>
      </div>
    );
  };
}

export function buildApexRenderer(ctxRef: MutableRefObject<ApexRendererCtx>) {
  const component = makeApexRenderer(ctxRef);
  return {
    language: [
      "apex", "apexclass", "apextrigger", "apextest",
      "java", "soql", "sql", "visualforce", "aura", "lwc",
      "html", "javascript", "typescript", "jsx", "tsx", "react", "css",
      "python", "bash", "shell", "sh", "zsh",
      "go", "rust", "ruby", "php", "kotlin", "swift", "c", "cpp", "csharp",
    ] as string[],
    component,
  };
}
